import type { ObraDetail, ReportMedia, ReportSummary } from '@obras/shared';
import {
  formatCurrency,
  formatCurrencyDelta,
  formatDate,
  formatDays,
  formatDuration,
  ratio,
  REPORT_TYPE_META,
} from '@obras/shared';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  ChartLine,
  Image as ImageIcon,
  Images,
  PauseCircle,
  Play,
  PlayCircle,
  Undo2,
  Video,
} from 'lucide-react';
import { AnimatePresence, motion, type Variants } from 'motion/react';
import { useMemo, useState, type FormEvent } from 'react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card, CardHeader, Kpi } from '../../components/ui/Card.js';
import { CoverImage, CoverPlaceholder } from '../../components/ui/Cover.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { EmptyState, ErrorState, ProgressBar, Skeleton } from '../../components/ui/Feedback.js';
import { Lightbox, useLightbox } from '../../components/ui/Lightbox.js';
import { Modal } from '../../components/ui/Modal.js';
import { Tab, TabList } from '../../components/ui/Tabs.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { cn } from '../../lib/cn.js';
import { useEndStoppage, useObraLedger, useStartStoppage, useStoppages } from './api.js';
import { montarAcervo, paraSlides } from './acervo.js';
import { VideoEmEspera } from './VideoEstado.js';

const ICON = { strokeWidth: 1.5 } as const;

const DELTA_TONE: Record<string, string> = {
  muted: 'text-ink-3',
  success: 'text-success',
  info: 'text-info',
  danger: 'text-danger',
  warning: 'text-warning',
};

// ────────────────────────────────────────────────────────── financeiro

/**
 * O dinheiro da obra: os indicadores, o progresso de pagamento e o livro-razão.
 *
 * O razão é só leitura (ADR 060). Lançamento errado se corrige pelo relatório
 * que o lançou — editar o valor ou remover o relatório lança o estorno no mesmo
 * ato —, e aqui aparecem os três: o errado riscado, o estorno e o certo.
 */
export function ObraFinanceTab({ obra }: { obra: ObraDetail }) {
  const { data, isPending, isError, error, refetch } = useObraLedger(obra.id);

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-28 rounded-none" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-none" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Erro desconhecido.'}
        onRetry={() => void refetch()}
      />
    );
  }

  const paidRatio = ratio(data.paidValueCents, data.currentValueCents);
  const glossRatio = ratio(data.glossValueCents, data.currentValueCents);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Valor inicial" value={formatCurrency(data.initialValueCents)} />
        <Kpi
          label="Aditivo"
          value={formatCurrencyDelta(data.additionsCents)}
          tone={data.additionsCents > 0 ? 'primary' : 'default'}
        />
        <Kpi
          label="Supressão"
          value={formatCurrencyDelta(-data.suppressionsCents)}
          tone={data.suppressionsCents > 0 ? 'danger' : 'default'}
        />
        <Kpi label="Valor final total" value={formatCurrency(data.currentValueCents)} tone="ink" />
      </div>

      <Card square>
        <CardHeader
          title="Progresso de pagamento"
          description="Calculado sobre o valor final do contrato."
        />
        <div className="px-5 pt-1 pb-5">
          <ProgressBar value={paidRatio} label="Pago" tone="success" />

          <div className="tabular mt-3 flex flex-wrap justify-between gap-2 text-[13px]">
            <span className="flex items-center gap-1.5 text-success">
              <span className="size-2 bg-success" aria-hidden />
              Pago: {formatCurrency(data.paidValueCents)}
            </span>

            {data.glossValueCents > 0 ? (
              <span className="flex items-center gap-1.5 text-warning">
                <span className="size-2 bg-warning" aria-hidden />
                Glosa: {formatCurrency(data.glossValueCents)} ({(glossRatio * 100).toFixed(1)}%)
              </span>
            ) : null}

            <span className="text-ink-3">
              Saldo: {formatCurrency(data.remainingCents)} de{' '}
              {formatCurrency(data.currentValueCents)}
            </span>
          </div>
        </div>
      </Card>

      <Card square className="overflow-x-auto">
        <CardHeader
          title="Livro-razão"
          description="Todo lançamento é permanente. Corrigir ou remover um relatório lança o estorno sozinho, e o errado fica riscado ao lado do certo."
        />

        {data.movements.length === 0 ? (
          <EmptyState
            icon={ChartLine}
            title="Nenhuma movimentação"
            description="Medições, aditivos, supressões e glosas aparecem aqui assim que forem registrados."
          />
        ) : (
          <table className="w-full min-w-[880px] text-left">
            <caption className="sr-only">Movimentações financeiras e de prazo da obra</caption>
            <thead className="bg-sunken">
              <tr className="text-[11px] font-medium tracking-[0.04em] text-ink-3 uppercase *:border-b-2 *:border-line-2">
                <th scope="col" className="px-5 py-3">
                  Data
                </th>
                <th scope="col" className="px-5 py-3">
                  Movimentação
                </th>
                <th scope="col" className="px-5 py-3">
                  Relatório
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  Variação
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  Saldo do contrato
                </th>
              </tr>
            </thead>
            <tbody>
              {data.movements.map((movement) => {
                const meta = REPORT_TYPE_META[movement.type];
                const isReversal = movement.reversalOfId !== null;
                const isReversed = movement.reversedByMovementId !== null;

                return (
                  <tr
                    key={movement.id}
                    className={cn(
                      'border-t border-line',
                      (isReversal || isReversed) && 'bg-sunken',
                    )}
                  >
                    <td className="tabular px-5 py-3 font-mono text-[13px]">
                      {formatDate(movement.effectiveDate)}
                    </td>

                    <td className="px-5 py-3">
                      <span className={cn('font-medium', DELTA_TONE[meta.tone])}>{meta.label}</span>
                      {isReversal ? (
                        <Badge tone="muted" icon={Undo2} className="ml-2">
                          Estorno
                        </Badge>
                      ) : null}
                      {isReversed ? (
                        <Badge tone="muted" className="ml-2">
                          Estornado
                        </Badge>
                      ) : null}
                    </td>

                    <td className="px-5 py-3 text-[13px] text-ink-3">
                      <span className="block truncate">{movement.reportTitle ?? '—'}</span>
                      {movement.authorName ? (
                        <span className="block text-[11px] text-ink-3">{movement.authorName}</span>
                      ) : null}
                    </td>

                    <td
                      className={cn(
                        'tabular px-5 py-3 text-right font-semibold',
                        DELTA_TONE[meta.tone],
                        isReversed && 'line-through opacity-60',
                      )}
                    >
                      {movement.days !== null
                        ? `${movement.days > 0 ? '+' : ''}${formatDays(movement.days)}`
                        : formatCurrencyDelta(movement.amountCents)}
                    </td>

                    <td className="tabular px-5 py-3 text-right text-[13px]">
                      {formatCurrency(movement.newValueCents)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────── galeria

/** O que a galeria mostra. */
type FiltroDeMidia = 'tudo' | 'imagem' | 'video';

const FILTROS: readonly { valor: FiltroDeMidia; rotulo: string; icone: LucideIcon }[] = [
  { valor: 'tudo', rotulo: 'Tudo', icone: Images },
  { valor: 'imagem', rotulo: 'Fotos', icone: ImageIcon },
  { valor: 'video', rotulo: 'Vídeos', icone: Video },
];

/** Até onde a fila da galeria vai: do nono quadro em diante, todos entram juntos (§10). */
const QUADROS_EM_FILA = 8;
const PASSO_DA_FILA = 0.04;

/**
 * A grade da galeria entra e sai como um bloco: trocar o filtro ou a ordem
 * apaga a anterior subindo um fio, em 160ms, e só então a nova entra.
 */
const GRADE: Variants = {
  hidden: {},
  shown: {},
  gone: { opacity: 0, y: -6, transition: { duration: 0.16, ease: EASE_OUT_QUINT } },
};

/** Cada quadro sobe 10px e assenta no seu passo da fila. */
const QUADRO: Variants = {
  hidden: { opacity: 0, y: 10 },
  shown: (posicao: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.36,
      ease: EASE_OUT_QUINT,
      delay: Math.min(posicao, QUADROS_EM_FILA) * PASSO_DA_FILA,
    },
  }),
};

/**
 * O acervo visual da obra.
 *
 * Junta foto e vídeo de todos os relatórios num lugar só — é onde se responde
 * "como estava a obra em maio?" sem abrir relatório por relatório. Daí os dois
 * controles: o filtro, para quem procura só o vídeo daquela vistoria, e a
 * ordem, porque conferir o histórico pede o mais antigo primeiro enquanto
 * acompanhar o andamento pede o contrário.
 */
export function ObraGalleryTab({ reports }: { reports: readonly ReportSummary[] }) {
  const lightbox = useLightbox();
  const [origem, setOrigem] = useState<DOMRect | null>(null);
  const [filtro, setFiltro] = useState<FiltroDeMidia>('tudo');
  const [recentesPrimeiro, setRecentesPrimeiro] = useState(true);

  // A ordem é a da foto, e só na falta dela a da visita — ver `montarAcervo`.
  const acervo = useMemo(
    () => montarAcervo(reports, recentesPrimeiro),
    [reports, recentesPrimeiro],
  );

  const contagem = {
    tudo: acervo.length,
    imagem: acervo.filter((item) => item.kind === 'imagem').length,
    video: acervo.filter((item) => item.kind === 'video').length,
  };

  const visiveis = acervo.filter((item) => filtro === 'tudo' || item.kind === filtro);

  const slides = paraSlides(visiveis);

  // Acervo vazio é outra coisa de filtro vazio: um pede envio, o outro, ajuste.
  if (acervo.length === 0) {
    return (
      <Card square>
        <EmptyState
          icon={Images}
          title="Nenhuma foto ou vídeo"
          description="As fotos e os vídeos enviados nos relatórios de visita aparecem aqui, com data e coordenada quando disponíveis."
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TabList label="Filtrar a galeria">
          {FILTROS.map((item) => (
            <Tab
              key={item.valor}
              active={filtro === item.valor}
              onClick={() => setFiltro(item.valor)}
              icon={item.icone}
              count={contagem[item.valor]}
              disabled={contagem[item.valor] === 0 && item.valor !== 'tudo'}
            >
              {item.rotulo}
            </Tab>
          ))}
        </TabList>

        <Button
          size="sm"
          onClick={() => setRecentesPrimeiro((atual) => !atual)}
          icon={
            recentesPrimeiro ? (
              <ArrowDownWideNarrow {...ICON} size={15} />
            ) : (
              <ArrowUpNarrowWide {...ICON} size={15} />
            )
          }
        >
          {recentesPrimeiro ? 'Mais recentes' : 'Mais antigos'}
        </Button>
      </div>

      {visiveis.length === 0 ? (
        <Card square>
          <EmptyState
            icon={filtro === 'video' ? Video : ImageIcon}
            title={filtro === 'video' ? 'Nenhum vídeo' : 'Nenhuma foto'}
            description="Nenhum item deste tipo foi enviado nos relatórios desta obra."
          />
        </Card>
      ) : (
        /*
         * Chaveada pelo filtro e pela ordem: outro recorte é outra grade, que
         * entra de novo em fila. Ao abrir a aba, a primeira também entra assim —
         * as fotos não aparecem todas de uma vez.
         */
        <AnimatePresence mode="wait">
          <motion.div
            key={`${filtro}-${recentesPrimeiro ? 'recentes' : 'antigos'}`}
            variants={GRADE}
            initial="hidden"
            animate="shown"
            exit="gone"
            className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          >
            {visiveis.map((item, position) => (
              <motion.div key={item.id} variants={QUADRO} custom={position}>
                <GalleryItem
                  item={item}
                  legenda={formatDate(item.visitDate)}
                  onOpen={(retangulo) => {
                    setOrigem(retangulo);
                    lightbox.open(position);
                  }}
                />
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      )}

      <Lightbox
        items={slides}
        index={lightbox.index}
        onClose={lightbox.close}
        onNavigate={lightbox.navigate}
        origin={origem}
      />
    </div>
  );
}

/**
 * Um quadro da galeria. Vídeo ganha play, duração e os estados do Bunny.
 *
 * Quadrado e sem zoom: sob o ponteiro a data aparece no pé e a barra laranja
 * cresce do meio, o gesto do cartão de obra. A foto aberta cresce do quadro até
 * o centro da tela. Enquanto baixa, o quadro é o visor da câmera procurando o
 * foco, e a foto chega saindo do desfoque — a mesma chegada da capa do cartão
 * (`<CoverImage>`), em vez de surgir de estalo.
 */
function GalleryItem({
  item,
  legenda,
  onOpen,
}: {
  item: ReportMedia & { reportTitle: string };
  legenda: string;
  onOpen: (origem: DOMRect) => void;
}) {
  const video = item.kind === 'video';

  if (video && item.status && item.status !== 'pronto') {
    return <VideoEmEspera mediaId={item.id} status={item.status} />;
  }

  return (
    <button
      type="button"
      onClick={(event) => onOpen(event.currentTarget.getBoundingClientRect())}
      aria-label={`Ver ${item.reportTitle}`}
      className="group relative block aspect-square w-full overflow-hidden bg-sunken"
    >
      {video && !item.thumbnailUrl ? (
        <CoverPlaceholder busy={false} />
      ) : (
        <CoverImage src={video ? (item.thumbnailUrl ?? '') : item.url} />
      )}

      {video ? (
        <>
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid size-11 place-items-center bg-black/55 text-white transition-colors group-hover:bg-black/70">
              <Play size={17} strokeWidth={2} fill="currentColor" aria-hidden />
            </span>
          </span>
          {item.durationSeconds ? (
            <span className="tabular absolute top-0 right-0 bg-black/65 px-1.5 py-0.5 text-[11px] font-medium text-white">
              {formatDuration(item.durationSeconds)}
            </span>
          ) : null}
        </>
      ) : null}

      <span className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-2 py-1 text-left font-mono text-[11px] text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        {legenda}
      </span>
      <span
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-0.5 origin-center scale-x-0 bg-primary transition-transform duration-300 ease-out-quint group-hover:scale-x-100 group-focus-visible:scale-x-100 motion-reduce:transition-none"
      />
    </button>
  );
}

// ────────────────────────────────────────────────────── paralisações

export function ObraStoppagesTab({ obra }: { obra: ObraDetail }) {
  const { can } = useAuth();
  const { data, isPending, isError, error, refetch } = useStoppages(obra.id);
  const start = useStartStoppage(obra.id);
  const end = useEndStoppage(obra.id);

  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [startedAt, setStartedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [endedAt, setEndedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const active = data?.find((item) => item.endedAt === null);
  const canWrite = can('fiscal');

  if (isPending) return <Skeleton className="h-48 rounded-none" />;
  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Erro desconhecido.'}
        onRetry={() => void refetch()}
      />
    );
  }

  async function submitStart(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);

    try {
      await start.mutateAsync({
        startedAt,
        reason,
        description: description.trim() === '' ? undefined : description,
      });
      setStartOpen(false);
      setReason('');
      setDescription('');
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Não foi possível paralisar a obra.');
    }
  }

  async function submitEnd(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);

    try {
      await end.mutateAsync({ endedAt, resumeStatus: 'em_andamento' });
      setEndOpen(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Não foi possível retomar a obra.');
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {active ? (
        <Card square className="bg-paused-soft p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 font-medium text-paused">
                <PauseCircle {...ICON} size={18} aria-hidden />
                Obra paralisada há {formatDays(active.durationDays)}
              </p>
              <p className="mt-1 text-[13px] text-ink-3">
                Desde {formatDate(active.startedAt)} · {active.reason}
              </p>
            </div>

            {canWrite ? (
              <Button
                variant="success"
                icon={<PlayCircle {...ICON} size={16} />}
                onClick={() => setEndOpen(true)}
              >
                Retomar obra
              </Button>
            ) : null}
          </div>
        </Card>
      ) : (
        <div className="flex justify-end">
          {canWrite && obra.status !== 'concluida' && obra.status !== 'cancelada' ? (
            <Button
              variant="secondary"
              icon={<PauseCircle {...ICON} size={16} />}
              onClick={() => setStartOpen(true)}
            >
              Registrar paralisação
            </Button>
          ) : null}
        </div>
      )}

      {data.length === 0 ? (
        <Card square>
          <EmptyState
            icon={PauseCircle}
            title="Nenhuma paralisação registrada"
            description="O histórico de paralisações desta obra aparece aqui, com o tempo total parado."
          />
        </Card>
      ) : (
        <Card square>
          <ul className="divide-y divide-line">
            {data.map((stoppage) => (
              <li key={stoppage.id} className="flex flex-wrap items-start gap-3 px-5 py-4">
                <PauseCircle
                  {...ICON}
                  size={18}
                  className={cn('mt-0.5 shrink-0', stoppage.endedAt ? 'text-ink-3' : 'text-paused')}
                  aria-hidden
                />

                <div className="min-w-0 flex-1">
                  <p className="font-medium">{stoppage.reason}</p>
                  {stoppage.description ? (
                    <p className="mt-1 text-[13px] text-ink-3">{stoppage.description}</p>
                  ) : null}
                  <p className="tabular mt-1 font-mono text-[11px] text-ink-3">
                    {formatDate(stoppage.startedAt)} →{' '}
                    {stoppage.endedAt ? formatDate(stoppage.endedAt) : 'em vigor'}
                    {stoppage.createdBy ? ` · ${stoppage.createdBy.name}` : ''}
                  </p>
                </div>

                <Badge tone={stoppage.endedAt ? 'muted' : 'paused'}>
                  {formatDays(stoppage.durationDays)}
                </Badge>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Modal
        open={startOpen}
        onClose={() => setStartOpen(false)}
        title="Registrar paralisação"
        description="A obra passa para a situação Paralisada até ser retomada."
        footer={
          <>
            <Button variant="ghost" onClick={() => setStartOpen(false)} disabled={start.isPending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              form="stoppage-form"
              type="submit"
              loading={start.isPending}
              icon={<PauseCircle {...ICON} size={16} />}
            >
              Paralisar obra
            </Button>
          </>
        }
      >
        <form
          id="stoppage-form"
          onSubmit={(event) => void submitStart(event)}
          className="flex flex-col gap-4"
          noValidate
        >
          {formError ? (
            <div role="alert" className="bg-danger-soft px-3.5 py-3 text-[13px] text-danger">
              {formError}
            </div>
          ) : null}

          <TextField
            label="Início da paralisação"
            type="date"
            mono
            required
            value={startedAt}
            onChange={(event) => setStartedAt(event.target.value)}
          />

          <TextField
            label="Motivo"
            required
            placeholder="Chuvas intensas, embargo, falta de material…"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />

          <TextAreaField
            label="Detalhamento"
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </form>
      </Modal>

      <Modal
        open={endOpen}
        onClose={() => setEndOpen(false)}
        title="Retomar obra"
        description="A obra volta para Em andamento a partir da data informada."
        footer={
          <>
            <Button variant="ghost" onClick={() => setEndOpen(false)} disabled={end.isPending}>
              Cancelar
            </Button>
            <Button
              variant="success"
              form="resume-form"
              type="submit"
              loading={end.isPending}
              icon={<PlayCircle {...ICON} size={16} />}
            >
              Retomar
            </Button>
          </>
        }
      >
        <form
          id="resume-form"
          onSubmit={(event) => void submitEnd(event)}
          className="flex flex-col gap-4"
          noValidate
        >
          {formError ? (
            <div role="alert" className="bg-danger-soft px-3.5 py-3 text-[13px] text-danger">
              {formError}
            </div>
          ) : null}

          <TextField
            label="Fim da paralisação"
            type="date"
            mono
            required
            value={endedAt}
            onChange={(event) => setEndedAt(event.target.value)}
          />
        </form>
      </Modal>
    </div>
  );
}
